import S from 's-js';
import { lift } from 's-array';
import UOM from 'lib/UOM';

import { PCCache } from 'data/PCCache';

import { Country } from 'data/models/Country';
import { NullableMeasurementData, NullableMeasurement } from 'data/models/Measurement';
import { OverrideServiceVessel } from 'data/models/OverrideServiceVessel';

import { ToggleableMeasurement } from 'portcall/toggleablemeasurement/model';

export interface ReadOnlyProperties {
    Length?: NullableMeasurementData,
    Beam?: NullableMeasurementData,
    Name?: string,
    IMO?: string,
    MMSID? : string,
    CallSign? : string,
    VIN? : string,
    GrossTonnage? : NullableMeasurementData,
    VesselFlagId? : number,
    VesselType? : string,
    MaxDraft?: NullableMeasurementData
}

export interface EditNewVesselOverridesParams {
    Overrides : OverrideServiceVessel;
    ReadOnly : () => ReadOnlyProperties;
    uom : () => string;
    onDismiss : () => void;
}

// TH: Specifies display-time precision for toggleable PC and Vessel fields Length, Beam, Air Draft, and Deep Draft
const lengthConversions = [
    {
        uom: UOM.Foot,
        // TH: To display as feet and inches instead of as decimal feet, uncomment the feetAndInches property and comment out the decimalPlaces property.
        // That requires CSS tweaks to make room for the wider field, so in the interest of getting something in, I'm holding off for now.
        //feetAndInches: true
        decimalPlaces: 2
    }, 
    {
        uom: UOM.Meter,
        decimalPlaces: 1
    }
];

export function EditNewVesselOverrides(
    data : PCCache,
    {
        Overrides,
        ReadOnly,
        uom,
        onDismiss
    } : EditNewVesselOverridesParams
) {
    const
        toggleableUom = S.value(S.sample(uom)), 
        ToggleableReadOnlyLength = S(() => ReadOnly().Length ? ToggleableMeasurement({
            backingMeasurement: new NullableMeasurement(ReadOnly().Length!),
            uom: toggleableUom,
            conversions: lengthConversions
        }) : null),
        ToggleableOverrideLength = ToggleableMeasurement({
            backingMeasurement: Overrides.Length,
            uom: toggleableUom,
            conversions: lengthConversions
        }),
        ToggleableReadOnlyBeam = S(() => ReadOnly().Beam ? ToggleableMeasurement({
            backingMeasurement: new NullableMeasurement(ReadOnly().Beam!),
            uom: toggleableUom,
            conversions: lengthConversions
        }) : null),
        ToggleableOverrideBeam = ToggleableMeasurement({
            backingMeasurement: Overrides.Beam,
            uom: toggleableUom,
            conversions: lengthConversions
        }),
        ToggleableReadOnlyMaxDraft = S(() => ReadOnly().MaxDraft ? ToggleableMeasurement({
            backingMeasurement: new NullableMeasurement(ReadOnly().MaxDraft!),
            uom: toggleableUom,
            conversions: lengthConversions
        }) : null),
        ToggleableOverrideMaxDraft = ToggleableMeasurement({
            backingMeasurement: Overrides.MaxDraft,
            uom: toggleableUom,
            conversions: lengthConversions
        }),
        UomInFeet = function (val? : boolean) {
            if (val === undefined) return toggleableUom() === UOM.Foot;
            else {
                var uom = val ? UOM.Foot : UOM.Meter;
                toggleableUom(uom);
                return val;
            }
        },
        Countries = lift(() => Array.from(data.Countries.items())).sort(Country.SortOrder);

    return { 
        Overrides,
        ReadOnly,
        ToggleableReadOnlyLength, 
        ToggleableOverrideLength, 
        ToggleableReadOnlyBeam, 
        ToggleableOverrideBeam,
        ToggleableReadOnlyMaxDraft,
        ToggleableOverrideMaxDraft,
        UomInFeet,
        Countries,
        Cancel: onDismiss
    };
};
export type EditNewVesselOverrides = ReturnType<typeof EditNewVesselOverrides>;


// WEBPACK FOOTER //
// ./apps/portcall/editnewvesseloverrides/model.ts